/**
 * ForecastResultCard.jsx
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-249 · Phase 5E-3a · Query Thread Render Extraction · forecast sub-region
 * Date:     2026-08-02
 * Umbrella: IJ-076 · Predictive Text Rebuild continuation · Refactor Programme 2026 · Phase 5E-3
 * Owner:    E1
 *
 * Purpose:
 *   Extract the forecast-prompt JSX sub-region out of the KnowledgeGraphPage.jsx
 *   queryThread render tree. First slice of the five-sub-slice Phase 5E-3
 *   programme. The block is guarded by `item.result.type === 'forecast_prompt'`
 *   and is fully self-contained: its state is only forecast-scoped · zero
 *   cross-cutting with the other queryThread sub-regions.
 *
 * Props:
 *   item                     · one queryThread entry · owns item.result.type and item.result.is_demo
 *   isAdmin                  · gates the upload toolbar (not the whole block)
 *   selectedFile             · currently-picked xlsx file
 *   includeFormulas          · checkbox state
 *   setIncludeFormulas       · setter
 *   isForecastLoading        · disables the Generate button
 *   forecastResult           · the generated forecast payload (null when not generated)
 *   forecastTooltip          · positional tooltip state for the Download button hover
 *   setForecastTooltip       · setter
 *   setForecastResult        · setter (× button dismisses the result panel)
 *   showDownloadCoachMark    · coach-mark visibility
 *   setShowDownloadCoachMark · coach-mark dismiss
 *   downloadForecastBtnAnchorRef · ref used to anchor the coach mark and portalled tooltip
 *   handleGenerateForecast       · click handler on Generate button
 *   handleInlineForecastUpload   · onChange handler on Upload input
 *   handleDownloadForecast       · click handler on Download button
 *
 * Byte-verbatim on every className · style · data-testid · axios URL ·
 * callback body. Zero behaviour change.
 *
 * StatusBadge and formatCurrency are duplicated inline (byte-verbatim from the
 * parent) to keep the extraction self-contained. A future micro-slice can pull
 * them into a shared utility once they have more callers.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { Upload, TrendingUp, Download, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { DemoDownloadCoachMark } from './DemoDownloadCoachMark';

const StatusBadge = ({ status, varianceAmount }) => {
  // Floating-point dust guard: any variance within $1 of zero is on-plan, regardless of upstream status
  const v = varianceAmount != null ? Number(varianceAmount) : null;
  if (v != null && Math.abs(v) < 1) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600/80 text-white text-xs font-bold"><CheckCircle className="h-3 w-3" />ON PLAN</span>;
  }
  // If green and variance is negative (under budget), show SURPLUS
  if (status === 'green' && v != null && v < 0) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600/80 text-white text-xs font-bold"><CheckCircle className="h-3 w-3" />SURPLUS</span>;
  }
  if (status === 'green') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600/80 text-white text-xs font-bold"><CheckCircle className="h-3 w-3" />ON PLAN</span>;
  if (status === 'yellow') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-600/80 text-white text-xs font-bold"><AlertTriangle className="h-3 w-3" />WATCH</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600/80 text-white text-xs font-bold"><XCircle className="h-3 w-3" />AT RISK</span>;
};

const formatCurrency = (val) => {
  if (val == null) return '—';
  const num = Number(val);
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(num);
};

export function ForecastResultCard({
  item,
  isAdmin,
  selectedFile,
  includeFormulas,
  setIncludeFormulas,
  isForecastLoading,
  forecastResult,
  forecastTooltip,
  setForecastTooltip,
  setForecastResult,
  showDownloadCoachMark,
  setShowDownloadCoachMark,
  downloadForecastBtnAnchorRef,
  handleGenerateForecast,
  handleInlineForecastUpload,
  handleDownloadForecast,
}) {
  if (item.result.type !== 'forecast_prompt') return null;
  return (
    <div className="mt-6 mx-auto" style={{ maxWidth: '85%' }} data-testid="forecast-controls">
      {!item.result.is_demo && isAdmin && (
      <div className="flex items-center gap-6 flex-wrap">
        {/* Upload Button */}
        <label className="inline-flex items-center gap-2 px-5 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg cursor-pointer transition-colors text-lg">
          <Upload className="h-6 w-6" />
          <span>Upload Excel File</span>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleInlineForecastUpload}
            className="hidden"
            data-testid="forecast-upload-input"
          />
        </label>
        
        {/* Formulas Checkbox */}
        <label className="inline-flex items-center gap-3 text-white text-lg cursor-pointer">
          <input
            type="checkbox"
            checked={includeFormulas}
            onChange={(e) => setIncludeFormulas(e.target.checked)}
            className="w-5 h-5 rounded border-gray-400 text-cyan-500 focus:ring-cyan-500"
            data-testid="include-formulas-checkbox"
          />
          <span>Include Formulas in Excel</span>
        </label>
        
        {/* Generate Button - appears after file selected */}
        {selectedFile && (
          <button
            onClick={handleGenerateForecast}
            disabled={isForecastLoading}
            className="inline-flex items-center gap-2 px-5 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-lg disabled:opacity-50"
            data-testid="generate-forecast-btn-inline"
          >
            {isForecastLoading ? 'Generating...' : 'Generate Forecast'}
          </button>
        )}
      </div>
      )}

      {/* Show selected file name */}
      {selectedFile && !item.result.is_demo && (
        <p className="mt-3 text-gray-300 text-sm">
          Selected: <span className="text-cyan-400">{selectedFile.name}</span>
        </p>
      )}
      
      {/* Forecast Result - appears here after generation */}
      {forecastResult && !isForecastLoading && (
        <div className="mt-8" data-testid="forecast-result-inline">
          {/* Header */}
          <div className="rounded-t-xl px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'rgba(80, 40, 130, 0.85)', backdropFilter: 'blur(12px)' }}>
            <div className="flex items-center gap-3">
              <TrendingUp className="h-6 w-6 text-purple-300" />
              <div>
                <h3 className="text-lg font-bold text-white">{forecastResult.title}</h3>
                <p className="text-xs text-purple-300">{forecastResult.category_count} line items analyzed</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={forecastResult.totals?.status} varianceAmount={forecastResult.totals?.variance_amount} />
              <div className="relative group" data-testid="download-forecast-tooltip-anchor" ref={downloadForecastBtnAnchorRef}>
                <DemoDownloadCoachMark
                  show={showDownloadCoachMark}
                  onDismiss={() => setShowDownloadCoachMark(false)}
                  anchorRef={downloadForecastBtnAnchorRef}
                />
                <button
                  onClick={() => handleDownloadForecast(forecastResult.excel_b64, forecastResult.title)}
                  onMouseEnter={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    setForecastTooltip({ top: r.bottom + 10, right: window.innerWidth - r.right });
                  }}
                  onMouseLeave={() => setForecastTooltip(null)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm font-semibold transition-colors"
                  data-testid="download-forecast-btn-inline"
                >
                  <Download className="h-4 w-4" />
                  Download Forecast (.xlsx)
                </button>
                {/* D6 fix (Feb 28, 2026): tooltip moved to a
                    React Portal at document.body to escape
                    the dashboard's backdrop-filter stacking
                    context — earlier z-[70] was trapped
                    inside the dashboard and hidden by
                    content cards below. */}
                {forecastTooltip && createPortal(
                  <div
                    className="fixed pointer-events-none z-[9999]"
                    style={{ top: forecastTooltip.top, right: forecastTooltip.right }}
                    data-testid="download-forecast-hover-tooltip"
                  >
                    <div
                      className="relative rounded-lg px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.55)] border border-cyan-300/70 whitespace-nowrap"
                      style={{
                        background: '#0B1530',
                      }}
                    >
                      <div
                        className="absolute right-6 -top-1.5 w-3 h-3 rotate-45 border-l border-t border-cyan-300/70"
                        style={{ background: '#0B1530' }}
                      />
                      <p className="text-xs text-white">
                        Download this forecast as .xlsx
                      </p>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
              <button onClick={() => setForecastResult(null)} className="text-gray-400 hover:text-white ml-1 text-xl">&times;</button>
            </div>
          </div>

          {/* Summary + Key Insights */}
          <div className="px-6 py-5" style={{ backgroundColor: 'rgba(50, 25, 90, 0.75)', backdropFilter: 'blur(12px)' }}>
            <h4 className="text-cyan-300 text-xs font-bold uppercase tracking-widest mb-2" data-testid="csuite-synopsis-label">C-Suite Synopsis</h4>
            <p className="text-gray-200 text-sm leading-relaxed mb-4" data-testid="csuite-synopsis-body">{forecastResult.summary}</p>
            {forecastResult.key_insights && forecastResult.key_insights.length > 0 && (
              <div className="mb-5">
                <h4 className="text-purple-300 text-xs font-bold uppercase tracking-widest mb-2">Key Insights</h4>
                <ul className="space-y-1">
                  {forecastResult.key_insights.map((insight, i) => (
                    <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                      <span className="text-purple-400 mt-0.5">▸</span>{insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Totals Cards */}
            {forecastResult.totals && (
              <div className="grid grid-cols-4 gap-4 mb-5">
                {(() => {
                  const va = forecastResult.totals.variance_amount || 0;
                  const vp = Number(forecastResult.totals.variance_pct || 0);
                  const isSurplus = va <= 0;
                  const absVa = Math.abs(va);
                  const absVp = Math.abs(vp);
                  return [
                    { label: 'Annual Budget', value: formatCurrency(forecastResult.totals.annual_budget), color: 'text-blue-300' },
                    { label: forecastResult.has_actuals ? 'Forecast (Actuals + Budget)' : 'Annual Forecast', value: formatCurrency(forecastResult.totals.annual_forecast), color: 'text-cyan-300' },
                    { label: isSurplus ? 'Budget Surplus' : 'Over Budget', value: formatCurrency(absVa), color: isSurplus ? 'text-green-300' : 'text-red-500' },
                    { label: 'Variance (%)', value: `${absVp.toFixed(1)}%`, color: absVp < 10 ? 'text-green-300' : absVp < 20 ? 'text-yellow-300' : 'text-red-500' },
                  ];
                })().map((card, i) => (
                  <div key={i} className="rounded-lg px-4 py-3" style={{ backgroundColor: 'rgba(70, 40, 110, 0.6)' }}>
                    <p className="text-purple-400 text-xs uppercase tracking-wide mb-1">{card.label}</p>
                    <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Forecast Composition Bar Graph (top categories) */}
            {forecastResult.categories && forecastResult.categories.length > 0 && (() => {
              const sorted = [...forecastResult.categories]
                .map(c => ({ name: c.name, value: Number(c.annual_forecast || 0) }))
                .filter(c => c.value > 0)
                .sort((a, b) => b.value - a.value);
              if (sorted.length === 0) return null;
              const top = sorted.slice(0, 6);
              const max = top[0].value || 1;
              const palette = ['bg-cyan-400', 'bg-purple-400', 'bg-emerald-400', 'bg-amber-400', 'bg-pink-400', 'bg-blue-400'];
              return (
                <div className="mb-5" data-testid="forecast-composition-graph">
                  <h4 className="text-purple-300 text-xs font-bold uppercase tracking-widest mb-3">Forecast Composition — Top Categories</h4>
                  <div className="rounded-lg px-4 py-4 space-y-2.5" style={{ backgroundColor: 'rgba(70, 40, 110, 0.45)' }}>
                    {top.map((row, i) => {
                      const pct = Math.max(2, (row.value / max) * 100);
                      return (
                        <div key={i} className="grid grid-cols-12 gap-3 items-center">
                          <div className="col-span-3 text-gray-200 text-xs truncate" title={row.name}>{row.name}</div>
                          <div className="col-span-7 h-3.5 rounded-full bg-purple-950/60 overflow-hidden">
                            <div
                              className={`h-full ${palette[i % palette.length]} transition-all duration-500`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="col-span-2 text-right text-cyan-300 text-xs font-semibold tabular-nums">{formatCurrency(row.value)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Categories Table with Traffic Lights */}
            {forecastResult.categories && forecastResult.categories.length > 0 && (
              <div className="mt-4">
                <h4 className="text-purple-300 text-xs font-bold uppercase tracking-widest mb-2">Line Item Summary</h4>
                <div className="rounded-lg overflow-hidden border border-purple-800/30">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-purple-400 text-xs uppercase tracking-wide border-b border-purple-800/30" style={{ backgroundColor: 'rgba(60, 30, 100, 0.5)' }}>
                        <th className="text-left px-3 py-2">Category</th>
                        <th className="text-right px-3 py-2">Budget</th>
                        {forecastResult.has_actuals && <th className="text-right px-3 py-2">Actuals</th>}
                        <th className="text-right px-3 py-2">Forecast</th>
                        <th className="text-right px-3 py-2">Variance</th>
                        <th className="text-center px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {forecastResult.categories.slice(0, 10).map((cat, i) => (
                        <tr key={i} className="border-b border-purple-900/20 hover:bg-purple-900/20 transition-colors">
                          <td className="px-3 py-2 text-gray-200">{cat.name}</td>
                          <td className="px-3 py-2 text-right text-blue-300">{formatCurrency(cat.annual_budget)}</td>
                          {forecastResult.has_actuals && <td className="px-3 py-2 text-right text-gray-300">{formatCurrency(cat.actuals_total || 0)}</td>}
                          <td className="px-3 py-2 text-right text-cyan-300">{formatCurrency(cat.annual_forecast)}</td>
                          {(() => {
                            // Treat values within $1 of zero as on-plan (kills floating-point dust)
                            const v = cat.variance_amount || 0;
                            const onPlan = Math.abs(v) < 1;
                            const overBudget = !onPlan && v > 0;
                            const cls = overBudget ? 'text-red-500' : 'text-green-300';
                            const display = onPlan ? formatCurrency(0) : (v < 0 ? formatCurrency(Math.abs(v)) : formatCurrency(v));
                            return <td className={`px-3 py-2 text-right ${cls}`}>{display}</td>;
                          })()}
                          <td className="px-3 py-2 text-center"><StatusBadge status={cat.status} varianceAmount={cat.variance_amount} size="sm" /></td>
                        </tr>
                      ))}
                      {forecastResult.categories.length > 10 && (
                        <tr>
                          <td colSpan={forecastResult.has_actuals ? 6 : 5} className="px-3 py-2 text-center text-gray-500 text-xs italic">
                            + {forecastResult.categories.length - 10} more line items in the downloaded Excel file
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Traffic Light Legend */}
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Traffic Light:</span>
                  <span className="inline-flex items-center gap-1 text-xs text-green-300"><CheckCircle className="h-3 w-3" /> Green = &lt;10% variance</span>
                  <span className="inline-flex items-center gap-1 text-xs text-yellow-300"><AlertTriangle className="h-3 w-3" /> Yellow = 10–20%</span>
                  <span className="inline-flex items-center gap-1 text-xs text-red-500"><XCircle className="h-3 w-3" /> Red = &gt;20%</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="rounded-b-xl px-6 py-3" style={{ backgroundColor: 'rgba(60, 30, 100, 0.85)' }}>
            <p className="text-gray-400 text-xs">Full 12-month breakdown available in the downloaded file</p>
          </div>
        </div>
      )}
    </div>
  );
}
